/**
 * SettingsContext
 * React context for application settings management
 * Persists user preferences to localStorage
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { isElectronMode } from '../utils/apiMode';
import { STORAGE_KEYS } from '../constants/storage';
import { selectFolder as electronBridgeSelectFolder } from '../utils/electronBridge';

export type ExportQuality = 'low' | 'medium' | 'high' | 'ultra';

/**
 * Settings for individual AI components (Ollama, Fooocus, etc.)
 */
export interface ComponentSettings {
  customPath?: string; // Custom installation path
  autoStart?: boolean; // Auto-start with app
  preferCloud?: boolean; // Prefer cloud when available
  lastStartedAt?: string; // ISO timestamp
}

export interface AppSettings {
  // Output settings
  defaultOutputPath: string;
  exportQuality: ExportQuality;
  // Notification settings
  audioNotificationsEnabled: boolean;
  desktopNotificationsEnabled: boolean;
  // Component preferences (per component)
  componentSettings: Record<string, ComponentSettings>;
}

interface SettingsContextValue {
  settings: AppSettings;
  updateSettings: (updates: Partial<AppSettings>) => void;
  resetSettings: () => void;
  isElectron: boolean;
  /**
   * Null when no persist has been attempted or the last persist succeeded.
   * Non-null when localStorage.setItem threw (quota exceeded, private-browsing
   * restriction, etc.) — surfaces a user-visible message.
   */
  persistError: string | null;
  selectOutputFolder: () => Promise<string | null>;
  /** Error from the last selectOutputFolder IPC call, if any. */
  folderPickerError: string | null;
  // Component settings helpers
  getComponentSettings: (componentId: string) => ComponentSettings;
  updateComponentSettings: (componentId: string, updates: Partial<ComponentSettings>) => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

const DEFAULT_SETTINGS: AppSettings = {
  defaultOutputPath: '',
  exportQuality: 'high',
  audioNotificationsEnabled: true,
  desktopNotificationsEnabled: true,
  componentSettings: {},
};

interface SettingsProviderProps {
  children: React.ReactNode;
}

/**
 * SettingsProvider
 * Provides settings context to all child components
 */
export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const isElectron = useMemo(() => isElectronMode(), []);

  // Initialize settings from localStorage
  const [settings, setSettings] = useState<AppSettings>(() => {
    if (typeof window === 'undefined') return DEFAULT_SETTINGS;
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SETTINGS);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.error('[SettingsContext] Error loading settings:', error);
    }
    return DEFAULT_SETTINGS;
  });

  /**
   * Non-null when the last localStorage.setItem failed so callers can surface
   * a message that the change won't survive a reload.
   */
  const [persistError, setPersistError] = useState<string | null>(null);

  /** Non-null when the last selectOutputFolder IPC call threw. */
  const [folderPickerError, setFolderPickerError] = useState<string | null>(null);

  // Persist settings to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
      setPersistError(null);
    } catch (error) {
      console.error('[SettingsContext] Error saving settings:', error);
      setPersistError(error instanceof Error ? error.message : String(error));
    }
  }, [settings]);

  // Update settings
  const updateSettings = useCallback((updates: Partial<AppSettings>) => {
    setSettings((current) => ({ ...current, ...updates }));
  }, []);

  // Reset to defaults
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    localStorage.removeItem(STORAGE_KEYS.SETTINGS);
    setPersistError(null);
  }, []);

  // Select output folder (Electron only)
  const selectOutputFolder = useCallback(async (): Promise<string | null> => {
    if (!isElectron) {
      console.warn('[SettingsContext] selectOutputFolder is only available in Electron');
      return null;
    }
    setFolderPickerError(null);
    const result = await electronBridgeSelectFolder();
    if (result.status === 'selected') {
      updateSettings({ defaultOutputPath: result.path });
      return result.path;
    }
    if (result.status === 'error') {
      console.error('[SettingsContext] Error selecting folder:', result.error);
      setFolderPickerError(result.error);
    }
    // 'cancelled' and 'unavailable' are silent
    return null;
  }, [isElectron, updateSettings]);

  // Get settings for a specific component
  const getComponentSettings = useCallback(
    (componentId: string): ComponentSettings => {
      return settings.componentSettings[componentId] || {};
    },
    [settings.componentSettings]
  );

  // Update settings for a specific component
  const updateComponentSettings = useCallback(
    (componentId: string, updates: Partial<ComponentSettings>) => {
      setSettings((current) => ({
        ...current,
        componentSettings: {
          ...current.componentSettings,
          [componentId]: {
            ...current.componentSettings[componentId],
            ...updates,
          },
        },
      }));
    },
    []
  );

  const contextValue = useMemo<SettingsContextValue>(
    () => ({
      settings,
      updateSettings,
      resetSettings,
      isElectron,
      persistError,
      folderPickerError,
      selectOutputFolder,
      getComponentSettings,
      updateComponentSettings,
    }),
    [
      settings,
      updateSettings,
      resetSettings,
      isElectron,
      persistError,
      folderPickerError,
      selectOutputFolder,
      getComponentSettings,
      updateComponentSettings,
    ]
  );

  return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
};

/**
 * Hook to use settings context
 * Throws error if used outside provider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useSettings = (): SettingsContextValue => {
  const context = useContext(SettingsContext);

  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }

  return context;
};
